// ==============================================================================
// DASHBOARD ESPECÍFICO - ADMINISTRADOR (IMPORTANDO TUS WIDGETS EXISTENTES)
// ==============================================================================

// src/components/dashboard/AdminDashboard.js
import React from 'react';
import { BaseDashboard } from './BaseDashboard';

// Imports de Material-UI (solo los necesarios para el widget de bienvenida)
import {
  Avatar,
  Box,
  Typography,
  Button,
  Grid
} from '@mui/material';

// Imports de iconos
import {
  Security as SecurityIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Assessment as ReportIcon
} from '@mui/icons-material';

// =============================================================================
// IMPORTAR TODOS TUS WIDGETS EXISTENTES
// =============================================================================

// Importar widgets específicos del administrador desde sus ubicaciones originales
import ActiveUsersWidget from '../admin/ActiveUsersWidget';
import AdminQuickActionsWidget from '../admin/AdminQuickActionsWidget';
import AdminStatsWidget from '../admin/AdminStatsWidget';
import EventsStatsWidget from '../admin/EventsStatsWidget';
import PlatformAnalyticsWidget from '../admin/PlatformAnalyticsWidget';
import RecentActivityWidget from '../admin/RecentActivityWidget';
import RevenueWidget from '../admin/RevenueWidget';
import SystemHealthWidget from '../admin/SystemHealthWidget';
import UserManagementWidget from '../admin/UserManagementWidget';

// =============================================================================
// WIDGET DE BIENVENIDA ESPECÍFICO (el único que creo aquí)
// =============================================================================

const AdminWelcomeWidget = ({ user }) => (
  <Box>
    <Box display="flex" alignItems="center" mb={3}>
      <Avatar sx={{ bgcolor: 'error.main', mr: 2, width: 56, height: 56 }}>
        🛡️
      </Avatar>
      <Box>
        <Typography variant="h5" gutterBottom>
          Bienvenido, {user?.displayName || 'Administrador'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Control total del sistema deportivo
        </Typography>
      </Box>
    </Box>
    
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Button 
          variant="contained" 
          fullWidth 
          size="small" 
          color="error"
          startIcon={<PeopleIcon />}
        >
          Panel Admin
        </Button>
      </Grid>
      <Grid item xs={6}>
        <Button 
          variant="outlined" 
          fullWidth 
          size="small" 
          color="error"
          startIcon={<SettingsIcon />}
        >
          Configurar
        </Button>
      </Grid>
    </Grid>
  </Box>
);

// =============================================================================
// COMPONENTE PRINCIPAL DEL DASHBOARD DEL ADMINISTRADOR
// =============================================================================

export const AdminDashboard = ({ user, onWidgetsChange, dashboardData }) => {
  // Configuración de widgets usando tus componentes existentes
  const adminWidgets = [
    { 
      id: 'admin-welcome', 
      title: null, // Sin título para que se vea más limpio
      size: { xs: 12, md: 8 }, 
      component: AdminWelcomeWidget,
      order: 1,
      props: { user }
    },
    { 
      id: 'system-health', 
      title: 'Salud del Sistema', 
      size: { xs: 12, md: 4 }, 
      component: SystemHealthWidget,
      order: 2,
      props: { 
        data: {
          uptime: dashboardData?.systemHealth?.uptime || 99.5,
          responseTime: dashboardData?.systemHealth?.responseTime || 120,
          errorsLast24h: dashboardData?.systemHealth?.errorsLast24h || 3,
          lastBackup: dashboardData?.systemHealth?.lastBackup || '2024-12-09 06:00:00'
        }
      }
    },
    { 
      id: 'admin-stats', 
      title: 'Estadísticas Generales', 
      size: { xs: 12, lg: 8 }, 
      component: AdminStatsWidget,
      order: 3,
      props: { 
        data: {
          totalUsers: dashboardData?.stats?.totalUsers || 1250,
          activeUsers: dashboardData?.stats?.activeUsers || 1094,
          totalEvents: dashboardData?.stats?.totalEvents || 45,
          upcomingEvents: dashboardData?.stats?.upcomingEvents || 12,
          completedEvents: dashboardData?.stats?.completedEvents || 33,
          totalRegistrations: dashboardData?.stats?.totalRegistrations || 2340,
          totalRevenue: dashboardData?.stats?.totalRevenue || 125430,
          athletesCount: dashboardData?.stats?.athletesCount || 890,
          organizersCount: dashboardData?.stats?.organizersCount || 156,
          judgesCount: dashboardData?.stats?.judgesCount || 67,
          photographersCount: dashboardData?.stats?.photographersCount || 23
        }
      }
    },
    { 
      id: 'quick-actions', 
      title: 'Acciones Rápidas', 
      size: { xs: 12, lg: 4 }, 
      component: AdminQuickActionsWidget,
      order: 4,
      props: {}
    },
    { 
      id: 'user-management', 
      title: 'Gestión de Usuarios', 
      size: { xs: 12, md: 8 }, 
      component: UserManagementWidget,
      order: 5,
      props: { 
        data: dashboardData?.users || [
          {
            id: 1,
            name: 'Ana García',
            email: 'ana.garcia@email.com',
            role: 'atleta',
            status: 'active',
            joinDate: '2024-01-15',
            avatar: null
          },
          {
            id: 2,
            name: 'Carlos López',
            email: 'carlos.lopez@email.com',
            role: 'organizador',
            status: 'active',
            joinDate: '2024-02-20',
            avatar: null
          },
          {
            id: 3,
            name: 'María Rodríguez',
            email: 'maria.rodriguez@email.com',
            role: 'entrenador',
            status: 'inactive',
            joinDate: '2024-03-10',
            avatar: null
          }
        ]
      }
    },
    { 
      id: 'active-users', 
      title: 'Usuarios Activos', 
      size: { xs: 12, md: 4 }, 
      component: ActiveUsersWidget,
      order: 6,
      props: { 
        data: {
          online: dashboardData?.activeUsers?.online || 856,
          last24h: dashboardData?.activeUsers?.last24h || 1204,
          byRole: {
            atleta: dashboardData?.activeUsers?.byRole?.atleta || 650,
            organizador: dashboardData?.activeUsers?.byRole?.organizador || 98,
            cronometrador: dashboardData?.activeUsers?.byRole?.cronometrador || 45,
            entrenador: dashboardData?.activeUsers?.byRole?.entrenador || 38,
            juez: dashboardData?.activeUsers?.byRole?.juez || 25
          },
          peakHours: [9, 12, 15, 18, 21]
        }
      }
    },
    { 
      id: 'events-stats', 
      title: 'Estadísticas de Eventos', 
      size: { xs: 12, md: 6 }, 
      component: EventsStatsWidget,
      order: 7,
      props: { 
        data: {
          total: dashboardData?.events?.total || 45,
          completed: dashboardData?.events?.completed || 33,
          active: dashboardData?.events?.active || 8,
          upcoming: dashboardData?.events?.upcoming || 4,
          byCategory: {
            athletics: dashboardData?.events?.byCategory?.athletics || 18,
            swimming: dashboardData?.events?.byCategory?.swimming || 12,
            football: dashboardData?.events?.byCategory?.football || 9,
            basketball: dashboardData?.events?.byCategory?.basketball || 6
          }
        }
      }
    },
    { 
      id: 'revenue', 
      title: 'Ingresos y Ganancias', 
      size: { xs: 12, md: 6 }, 
      component: RevenueWidget,
      order: 8,
      props: { 
        data: {
          total: dashboardData?.revenue?.total || 125430,
          registrations: dashboardData?.revenue?.registrations || 78650,
          subscriptions: dashboardData?.revenue?.subscriptions || 46780,
          thisMonth: dashboardData?.revenue?.thisMonth || 47100,
          lastMonth: dashboardData?.revenue?.lastMonth || 39050,
          growth: dashboardData?.revenue?.growth || '18'
        }
      }
    },
    { 
      id: 'platform-analytics', 
      title: 'Analíticas de la Plataforma', 
      size: { xs: 12, md: 8 }, 
      component: PlatformAnalyticsWidget,
      order: 9,
      props: { 
        data: {
          pageViews: dashboardData?.analytics?.pageViews || 25680,
          uniqueVisitors: dashboardData?.analytics?.uniqueVisitors || 8934,
          bounceRate: dashboardData?.analytics?.bounceRate || 32.4,
          avgSessionDuration: dashboardData?.analytics?.avgSessionDuration || 245,
          topPages: [
            { page: '/dashboard', views: 8934, percentage: 34.8 },
            { page: '/events', views: 6723, percentage: 26.2 },
            { page: '/profile', views: 4521, percentage: 17.6 },
            { page: '/registration', views: 3456, percentage: 13.5 },
            { page: '/results', views: 1986, percentage: 7.9 }
          ]
        }
      }
    },
    { 
      id: 'recent-activity', 
      title: 'Actividad Reciente', 
      size: { xs: 12, md: 4 }, 
      component: RecentActivityWidget,
      order: 10,
      props: { 
        data: dashboardData?.recentActivity || [
          {
            id: 1,
            type: 'user_registered',
            description: 'Ana García se registró como nueva atleta',
            timestamp: '2024-12-09 14:45:00',
            metadata: { userType: 'atleta', location: 'México' }
          },
          {
            id: 2,
            type: 'event_created',
            description: 'Maratón Regional 2024 fue publicado',
            timestamp: '2024-12-09 14:15:00',
            metadata: { eventId: 'MAR2024', category: 'atletismo' }
          },
          {
            id: 3,
            type: 'payment_received',
            description: 'Pago de inscripción confirmado - $150 MXN',
            timestamp: '2024-12-09 13:30:00',
            metadata: { amount: 150, currency: 'MXN' }
          },
          {
            id: 4,
            type: 'system_backup',
            description: 'Backup automático completado exitosamente',
            timestamp: '2024-12-09 06:00:00',
            metadata: { size: '2.4GB', duration: '15min' }
          },
          {
            id: 5,
            type: 'security_alert',
            description: 'Detectados 3 intentos de login fallidos',
            timestamp: '2024-12-09 13:45:00',
            metadata: { attempts: 3, ip: '192.168.1.100' }
          }
        ]
      }
    }
  ];

  // Acciones personalizadas para el SpeedDial
  const customActions = [
    {
      icon: <SettingsIcon />,
      tooltip: "Configuración del Sistema",
      onClick: () => console.log('Abrir configuración del sistema')
    },
    {
      icon: <PeopleIcon />,
      tooltip: "Gestión de Usuarios",
      onClick: () => console.log('Abrir gestión de usuarios')
    },
    {
      icon: <ReportIcon />,
      tooltip: "Generar Reportes",
      onClick: () => console.log('Abrir reportes del sistema')
    },
    {
      icon: <SecurityIcon />,
      tooltip: "Auditoría de Seguridad",
      onClick: () => console.log('Abrir auditoría de seguridad')
    }
  ];

  return (
    <BaseDashboard
      role="admin"
      user={user}
      widgets={adminWidgets}
      title="Panel de Administración"
      subtitle="Control total de la plataforma deportiva"
      roleIcon="🛡️"
      roleColor="error"
      onWidgetsChange={onWidgetsChange}
      customActions={customActions}
    />
  );
};

export default AdminDashboard;